// helpers/brandAutopilot.js
//
// Frontend mirror of the worker's per-brand autopilot resolution (see
// prigmar-worker/utils/autopilot.js). Drives the wizard Step 05 "Settings"
// toggles so the checkbox a user sees matches what the worker will actually
// do for an unset feature:
//   - post/email/chat ride the brand-wide `autopost` switch when unset,
//   - enrich defaults ON when unset (re-enrichment has always run),
//   - every other feature is opt-in (defaults OFF) until explicitly enabled.
//
// The wizard stores explicit booleans the moment a toggle is flipped and
// sends the FULL merged map on Save, so the backend $set replaces the whole
// autopilot subdoc without dropping previously-set flags.

// Ordered list of the per-feature switches rendered in Step 05, with the
// human label + helper text shown next to each toggle.
export const AUTOPILOT_FEATURES = [
  { key: 'post',      label: 'Auto post',          hint: 'Publish generated social posts without approval.' },
  { key: 'email',     label: 'Auto email',         hint: 'Send generated email replies / sequences without approval.' },
  { key: 'chat',      label: 'Auto chat',          hint: 'Let the chat widget reply to visitors without approval.' },
  { key: 'crm',       label: 'Auto CRM',           hint: 'Run CRM management actions automatically.' },
  { key: 'community', label: 'Auto community',     hint: 'Weekly prospect-community crawl / subscribe / reply.' },
  { key: 'answer',    label: 'Auto FAQ answer',    hint: 'Write & publish FAQ answers automatically.' },
  { key: 'article',   label: 'Auto SEO article',   hint: 'Publish generated SEO articles automatically.' },
  { key: 'sequence',  label: 'Auto sequence',      hint: 'Run sequencing campaigns automatically.' },
  { key: 'review',    label: 'Auto review reply',  hint: 'Reply to Google Business reviews automatically.' },
  { key: 'enrich',    label: 'Auto enrich',        hint: 'Periodically refresh the AI brand profile. On by default.' },
];

// Features that historically rode `autopost` before per-feature switches existed.
const LEGACY_AUTOPOST = new Set(['post', 'email', 'chat']);

// The effective ON/OFF a feature has when its explicit switch is unset.
// `autopost` is the brand-wide auto-publish master (the wizard's `isAuto`).
export function effectiveAutopilotDefault(feature, { autopost } = {}) {
  if (feature === 'enrich') return true;          // re-enrichment defaults on
  if (LEGACY_AUTOPOST.has(feature)) return autopost !== false;
  return false;                                   // newer features: opt-in only
}

// Resolve what the checkbox should show: explicit value wins, else the
// effective default. `?? ` (not `||`) so an explicit `false` is honored.
export function resolveAutopilotChecked(autopilotFlags, feature, ctx) {
  const explicit = autopilotFlags && autopilotFlags[feature];
  if (typeof explicit === 'boolean') return explicit;
  return effectiveAutopilotDefault(feature, ctx);
}

// onChange handler factory for a per-feature autopilot switch.
//
// CRITICAL — read the checked value SYNCHRONOUSLY here, before handing a
// functional updater to setState. React 16 (this app's version) pools
// SyntheticEvents and releases `e` back to the pool the instant the handler
// returns; the functional updater, however, runs LATER during the render
// flush. Reading `e.target.checked` *inside* that deferred updater therefore
// dereferences an already-nulled `e.target` and throws
//   "Cannot read properties of null (reading 'checked')"
// — which is exactly what crashed the Auto enrich toggle in brand edit
// settings. The brand-wide toggles next to it (autoGenerate / autopost /
// autoNewsletter) never crashed because they read `e.target.checked` directly
// in the handler body. Capturing `checked` first makes the toggle pool-safe.
export function makeAutopilotToggleHandler(setAutopilotFlags, feature) {
  return (e) => {
    const checked = e.target.checked; // capture before the event is released
    setAutopilotFlags((prev) => ({ ...(prev || {}), [feature]: checked }));
  };
}
